import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchPublicAppointmentsPerListingData } from "./DataServices/AppointmentsDataServices";
import { getPersonDetails } from "./DataServices/SecurityDepositDataServices";
import { baseUrl } from "./DataServices/Configuration";
import Loading from "./Loading";

interface IListing {
  id: string;
  title: string;
  description: string;
  rent: string;
  moveOutDate: string;
  building: string;
  line: string;
  floor: number;
  numOfBedrooms: number;
}

interface IAvailableAppointment {
  id: string;
  startTime: Date | null;
  endTime: Date | null;
  capacity: number;
}

interface IPerson {
  id: string;
  name: string;
  email: string;
  type: string;
}

export default function SlotConfirmation() {
  const { listingId, personId } = useParams<{
    listingId: string;
    personId: string;
  }>();
  const [listing, setListing] = useState<IListing | null>(null);
  const [slots, setSlots] = useState<IAvailableAppointment[]>([]);
  const [person, setPerson] = useState<IPerson | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!listingId || !personId) {
        setError("Invalid link");
        setLoading(false);
        return;
      }

      try {
        // Fetch listing
        const listingResponse = await fetch(
          `${baseUrl}/api/Showings/PublicListing/${listingId}`,
        );
        if (!listingResponse.ok) {
          throw new Error("Listing not found");
        }
        const listingData = await listingResponse.json();
        setListing(listingData);

        // Fetch person
        const personData = await getPersonDetails(listingId, personId);
        setPerson(personData);

        // Fetch slots
        const slotsData =
          await fetchPublicAppointmentsPerListingData(listingId);
        const futureSlots = slotsData
          .filter((slot: any) => {
            if (!slot.endTime) return false;
            return new Date(slot.endTime) > new Date();
          })
          .sort((a: any, b: any) => {
            const aTime = new Date(a.startTime).getTime();
            const bTime = new Date(b.startTime).getTime();
            return aTime - bTime;
          });
        setSlots(futureSlots);

        // Fetch existing slot confirmation
        try {
          const confirmationResponse = await fetch(
            `${baseUrl}/api/Appointments/GetSlotConfirmation/${listingId}/${personId}`,
          );
          if (confirmationResponse.ok) {
            setSubmitted(true);
          }
        } catch (err) {
          // No existing confirmation found, continue normally
        }
      } catch (err: any) {
        setError(err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [listingId, personId]);

  const handleSubmit = async () => {
    if (submitted || slots.length === 0) return;

    const selectedSlotIds = slots.map((slot) => slot.id);

    try {
      const response = await fetch(`${baseUrl}/api/Appointments/ConfirmSlots`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          personId,
          selectedSlotIds,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to confirm slots");
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || "Failed to submit");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Error</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Showing Slot Confirmation
            </h1>
            {listing && (
              <p className="text-sm text-gray-500 mb-4">
                Property:{" "}
                <strong className="text-gray-700">{listing.title}</strong>
              </p>
            )}
            {person && (
              <p className="text-gray-700 mb-2">
                Hi <strong>{person.name}</strong>,
              </p>
            )}
            <p className="text-gray-600 leading-relaxed">
              Please confirm the showing slots below.
            </p>
          </div>

          {/* Success Message */}
          {submitted && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg flex items-start">
              <svg
                className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-semibold">Showing Slots Confirmed!</p>
                <p className="text-sm">
                  This showing link is already confirmed. No further action is
                  required.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && !submitted && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}

          {/* Available Slots */}
          {!submitted && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Showing Slots to Confirm:
              </h2>

              {slots.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No showing slots found for this property.
                </div>
              ) : (
                <div className="space-y-3">
                  {slots.map((slot) => {
                    return (
                      <div
                        key={slot.id}
                        className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/40 transition-all duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-gray-900">
                              {slot.startTime && slot.endTime
                                ? `${new Date(slot.startTime).toLocaleDateString()} ${new Date(slot.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - ${new Date(slot.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                                : "Invalid time"}
                            </h3>
                          </div>
                          <div className="ml-4"></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Submit Button */}
              {slots.length > 0 && (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleSubmit}
                    disabled={submitted}
                    className={`px-8 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                      submitted
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    Confirm Showing Slots
                  </button>
                </div>
              )}
            </div>
          )}

          {!submitted && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                If you have any questions or need assistance, please contact us.
              </p>
              <div className="mt-3 text-sm text-gray-700">
                <p className="font-semibold">The Downtown Apartments, LLC</p>
                <p>212-343-3014 | rental@thedowntownapts.com</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
